/*
 * ApplicationsPanel — single approval queue for TPs, Drivers, and Partners.
 *
 * Consolidates: Providers-page banner + Credentialing-Submissions "Open" buttons +
 * Driver-Applications legacy screen + Facility/Broker/Dispatcher partner leads.
 * ONE place to approve/reject/action every onboarding request.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { apiClient } from "../../lib/api";
import { toast } from "sonner";
import ApplicationReviewModal from "./ApplicationReviewModal";
import TPApprovedDialog from "./TPApprovedDialog";

const C = { forest: "#2B4C3B", ink: "#0F172A", dim: "#475569", line: "#E2E0D8", amber: "#B45309", red: "#B91C1C" };

const TABS = [
  { id: "tp", label: "Providers (TPs)", endpoint: "/tpt/applications?state=pending" },
  { id: "driver", label: "Drivers", endpoint: "/tpt/drivers/new-applications" },
  { id: "partners", label: "Partners (Facility/Broker/Dispatcher)", endpoint: "/partners/leads" },
];

// Per-tab action wiring — every tab has its own backend shape, but ONE UI.
const TAB_ACTIONS = {
  tp: {
    approveUrl: (id) => `/tpt/applications/${id}/approve`,
    rejectUrl: (id) => `/tpt/applications/${id}/reject`,
    approveLabel: "Approve", rejectLabel: "Reject", rejectConfirm: true,
  },
  driver: {
    approveUrl: (id) => `/tpt/drivers/${id}/approve`,
    rejectUrl: (id) => `/tpt/drivers/${id}/reject`,
    approveLabel: "Approve", rejectLabel: "Reject", rejectConfirm: true,
  },
  partners: {
    approveUrl: (id) => `/partners/leads/${id}/status?status=contacted`,
    rejectUrl: (id) => `/partners/leads/${id}/status?status=closed`,
    approveLabel: "Mark contacted", rejectLabel: "Close", rejectConfirm: false,
  },
};

const ROLE_LABEL = { facility: "Facility", broker: "Broker", dispatcher: "Dispatcher", other: "Other" };

export default function ApplicationsPanel({ embedded = false, onApproved } = {}) {
  const [tab, setTab] = useState("tp");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [reviewing, setReviewing] = useState(null); // { appType, appId, row } | null
  const [approvedTp, setApprovedTp] = useState(null);
  const seenAlertRef = useRef(new Set());
  const sinceAlertRef = useRef(new Date().toISOString());

  // Department note alerts — same dispatch.publish_event bus the onboarding
  // team-alerts use. Lives here (not a separate file) so it covers Admin's
  // standalone Applications page AND Floor's embedded ApprovalsLane with
  // one poller, since both render this same component.
  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await apiClient.get("/dispatch/recent-events?limit=25");
        const rows = Array.isArray(data) ? data : [];
        for (const e of rows) {
          if (e.kind !== "department_alert" || !e.at || e.at <= sinceAlertRef.current) continue;
          const key = `${e.kind}:${e.at}`;
          if (seenAlertRef.current.has(key)) continue;
          seenAlertRef.current.add(key);
          toast.info(`${e.department} alert: ${e.applicant_name || "an applicant"} — "${e.text || ""}"`, { duration: 8000 });
        }
      } catch {
        // alert toasts are a convenience — never block the panel on this
      }
    };
    poll();
    const t = setInterval(poll, 12000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async (id) => {
    const active = TABS.find(t => t.id === id) || TABS[0];
    setLoading(true);
    try {
      const { data } = await apiClient.get(active.endpoint);
      // Backend endpoints return various shapes: bare array, {items:[]}, or {applications:[]}
      const items = Array.isArray(data)
        ? data
        : (data.items || data.applications || []);
      // Partner leads endpoint returns everything — only show the "new" queue here.
      setRows(id === "partners" ? items.filter(r => r.status === "new") : items);
    } catch (e) {
      setRows([]);
      toast.error(`Could not load ${active.label}`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const approve = async (row) => {
    const rid = row.id || row.application_id;
    setBusyId(rid);
    try {
      const { data } = await apiClient.post(TAB_ACTIONS[tab].approveUrl(rid));
      toast.success(tab === "partners" ? "Marked contacted" : "Approved");
      load(tab);
      // Partners never go through MedexOva credentialing — only TPs/drivers
      // jump straight into Submissions. TPs first see their login + contract step.
      if (tab === "tp") setApprovedTp(data);
      else if (tab === "driver") onApproved?.("driver", rid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
      return false;
    } finally { setBusyId(null); }
  };

  const reject = async (row) => {
    const rid = row.id || row.application_id;
    const actions = TAB_ACTIONS[tab];
    let reason = "";
    if (actions.rejectConfirm) {
      const r = window.prompt(`Reject ${row.company_name || row.organization || row.name}?\n\nOptional reason (included in the text/email sent to the applicant):`, "");
      if (r === null) return false;
      reason = r.trim();
    }
    setBusyId(rid);
    try {
      await apiClient.post(actions.rejectUrl(rid), actions.rejectConfirm ? { reason } : undefined);
      toast.success(actions.rejectLabel === "Close" ? "Closed" : "Rejected — applicant notified");
      load(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
      return false;
    } finally { setBusyId(null); }
  };

  const actions = TAB_ACTIONS[tab];

  return (
    <div data-testid="applications-panel" className="space-y-4">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold" style={{ color: C.ink }}>Applications</h1>
          <div className="text-sm" style={{ color: C.dim }}>Single approval queue — every onboarding request (drivers, providers, and facility/broker/dispatcher leads) lands here. No more hunting across screens.</div>
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`applications-tab-${t.id}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors`}
            style={{
              background: tab === t.id ? C.forest : "#fff",
              color: tab === t.id ? "#fff" : C.ink,
              border: `1px solid ${tab === t.id ? C.forest : C.line}`,
            }}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="rounded-2xl bg-white border overflow-hidden" style={{ borderColor: C.line }}>
        {loading ? (
          <div className="p-6 text-center text-sm" style={{ color: C.dim }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm" style={{ color: C.dim }}>✅ Queue empty — nothing pending.</div>
        ) : (
          <table className="w-full text-sm">
            <thead style={{ background: "#F5F3EA" }}>
              <tr className="text-left" style={{ color: C.dim }}>
                <th className="p-3 text-xs uppercase tracking-wider">Name</th>
                {tab === "partners" && <th className="p-3 text-xs uppercase tracking-wider">Role</th>}
                <th className="p-3 text-xs uppercase tracking-wider">Contact</th>
                <th className="p-3 text-xs uppercase tracking-wider">Applied</th>
                <th className="p-3 text-xs uppercase tracking-wider text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const rid = r.id || r.application_id;
                const busy = busyId === rid;
                return (
                  <tr key={rid} className="border-t" style={{ borderColor: C.line }} data-testid={`app-row-${rid}`}>
                    <td className="p-3">
                      <div className="font-semibold">{r.company_name || r.organization || r.name || r.contact_name || "—"}</div>
                      {tab === "partners" && r.contact_name && (
                        <div className="text-xs" style={{ color: C.dim }}>{r.contact_name}{r.facility_type ? ` · ${r.facility_type}` : ""}</div>
                      )}
                    </td>
                    {tab === "partners" && (
                      <td className="p-3 text-xs font-semibold" style={{ color: C.forest }}>{ROLE_LABEL[r.role] || r.role || "—"}</td>
                    )}
                    <td className="p-3 text-xs" style={{ color: C.dim }}>
                      {r.email || r.contact_email || "—"}<br/>{r.phone || r.contact_phone || ""}
                    </td>
                    <td className="p-3 text-xs" style={{ color: C.dim }}>{(r.created_at || r.applied_at || "").slice(0, 10)}</td>
                    <td className="p-3 text-right space-x-2">
                      {(tab === "tp" || tab === "driver") && (
                        <button onClick={() => setReviewing({ appType: tab, appId: rid, row: r })} data-testid={`review-${rid}`}
                          className="px-3 py-1.5 rounded-md text-xs font-semibold border"
                          style={{ borderColor: C.forest, color: C.forest }}>Call &amp; Review</button>
                      )}
                      <button onClick={() => approve(r)} disabled={busy} data-testid={`approve-${rid}`}
                        className="px-3 py-1.5 rounded-md text-xs font-bold text-white disabled:opacity-60"
                        style={{ background: C.forest }}>{busy ? "…" : actions.approveLabel}</button>
                      <button onClick={() => reject(r)} disabled={busy} data-testid={`reject-${rid}`}
                        className="px-3 py-1.5 rounded-md text-xs font-semibold border disabled:opacity-60"
                        style={{ borderColor: C.line, color: C.red }}>{actions.rejectLabel}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {reviewing && (
        <ApplicationReviewModal
          appType={reviewing.appType}
          appId={reviewing.appId}
          onClose={() => setReviewing(null)}
          onApprove={() => approve(reviewing.row)}
          onReject={() => reject(reviewing.row)}
        />
      )}
      {approvedTp && (
        <TPApprovedDialog
          data={approvedTp}
          onClose={() => { const id = approvedTp.tp_id; setApprovedTp(null); onApproved?.("tp", id); }}
        />
      )}
    </div>
  );
}
